import { NavLink, useLocation } from "react-router-dom";
import { useApp } from "@/context/AppContext";
import { useAuth } from "@/context/AuthContext";
import { useEffect, useState } from "react";
import { api, errMsg } from "@/lib/api";
import { toast } from "sonner";
import {
  LayoutDashboard, Egg, Wheat, Syringe, Truck, FileBarChart2,
  Moon, Sun, AlertTriangle, Menu, X, Warehouse, Settings, TrendingUp, LogOut, KeyRound, ArrowLeftRight, Gauge, Camera,
} from "lucide-react";
import { cn } from "@/lib/utils";
import ChatWidget from "@/components/ChatWidget";
import InstallPWA from "@/components/InstallPWA";
import SyncManager from "@/components/SyncManager";

const SATIS_PATHS = ["/satis-panel", "/sevkiyat", "/depo-transfer"];

const NAV = [
  { to: "/kumes", label: "Kontrol Paneli", icon: LayoutDashboard, testid: "nav-dashboard" },
  { to: "/uretim", label: "Günlük Üretim", icon: Egg, testid: "nav-production" },
  { to: "/yem", label: "Yem & Stok", icon: Wheat, testid: "nav-feed" },
  { to: "/ilac", label: "İlaç & Aşı", icon: Syringe, testid: "nav-medicine" },
  { to: "/satis-panel", label: "Kontrol Paneli", icon: LayoutDashboard, testid: "nav-sales-dashboard" },
  { to: "/sevkiyat", label: "Satış", icon: Truck, testid: "nav-shipment", perm: ["manage_shipping", "view_customers", "view_financials"] },
  { to: "/depo-transfer", label: "Depolar Arası", icon: ArrowLeftRight, testid: "nav-transfer" },
  { to: "/ongoru", label: "Stok", icon: TrendingUp, testid: "nav-forecast" },
  { to: "/randiman-fire", label: "Randıman & Fire", icon: Gauge, testid: "nav-yield-loss" },
  { to: "/medya", label: "Kümes Medya", icon: Camera, testid: "nav-media" },
  { to: "/raporlar", label: "Raporlar", icon: FileBarChart2, testid: "nav-reports", adminOnly: true },
];

export default function Layout({ children, onBackToModules }) {
  const { theme, toggleTheme, coops, selectedCoop, setSelectedCoop, isOperator } = useApp();
  const { user, logout } = useAuth();
  const [alerts, setAlerts] = useState([]);
  const [open, setOpen] = useState(false);
  const [showAlerts, setShowAlerts] = useState(false);
  const [showPw, setShowPw] = useState(false);
  const [pw, setPw] = useState({ current: "", next: "", confirm: "" });
  const location = useLocation();
  const isSatis = SATIS_PATHS.includes(location.pathname);

  const changePw = async () => {
    if (pw.next.length < 6) return toast.error("Yeni şifre en az 6 karakter olmalı");
    if (pw.next !== pw.confirm) return toast.error("Şifreler eşleşmiyor");
    try {
      await api.post("/auth/change-password", { current_password: pw.current, new_password: pw.next });
      toast.success("Şifreniz güncellendi");
      setShowPw(false);
      setPw({ current: "", next: "", confirm: "" });
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => {
    api.get("/alerts").then(({ data }) => setAlerts(data)).catch(() => {});
  }, [location.pathname]);

  const activeCoops = coops.filter((c) => c.type === "coop");
  const groups = [...new Set(activeCoops.filter((c) => c.group).map((c) => c.group))];
  const coopPicker = (sfx = "") => (isOperator ? (
    <div className="h-9 flex items-center gap-2 rounded-lg border border-input bg-secondary/50 px-3 text-sm font-medium truncate" data-testid={`coop-locked${sfx}`}>
      <Egg size={14} className="text-primary shrink-0" />
      <span className="truncate">{coops.find((c) => c.id === selectedCoop)?.name || "Kümesim"}</span>
    </div>
  ) : (
    <select value={selectedCoop} onChange={(e) => setSelectedCoop(e.target.value)} data-testid={`coop-selector${sfx}`}
      className="h-9 w-full sm:w-auto rounded-lg border border-input bg-background px-3 text-sm font-medium sm:max-w-[220px] focus:ring-2 focus:ring-primary outline-none">
      <option value="all">Tüm Kümesler</option>
      {groups.map((g) => (
        <option key={g} value={`group:${g}`}>{`${g} Kümes (Konsolide)`}</option>
      ))}
    </select>
  ));

  return (
    <div className="min-h-screen flex bg-background">
      {/* Sidebar */}
      <aside
        className={cn(
          "fixed lg:static inset-y-0 left-0 z-40 w-64 border-r border-border bg-card flex flex-col transition-transform duration-200",
          open ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
        data-testid="sidebar"
      >
        <div className="h-16 flex items-center gap-2.5 px-5 border-b border-border box-content pt-safe">
          <img src="https://customer-assets-lxgj4vgw.emergentagent.net/job_coop-control-11/artifacts/r51dsgro_image.png" alt="MİM" className="h-9 w-9 rounded-lg object-cover" />
          <div className="leading-tight">
            <div className="font-head font-extrabold text-[15px] tracking-tight">MİM GIDA</div>
            <div className="text-[10px] uppercase tracking-[0.15em] text-muted-foreground">Kümes Takip</div>
          </div>
        </div>
        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.filter((n) => SATIS_PATHS.includes(n.to) === isSatis && (!n.adminOnly || user?.role === "admin") && (!n.perm || user?.role === "admin" || n.perm.some((p) => user?.permissions?.[p]))).map((n) => (
            <NavLink
              key={n.to}
              to={n.to}
              end={n.to === "/kumes"}
              onClick={() => setOpen(false)}
              data-testid={n.testid}
              className={({ isActive }) =>
                cn(
                  "flex items-center gap-3 px-3 py-2.5 rounded-lg text-sm font-medium transition-colors",
                  isActive
                    ? "bg-primary text-primary-foreground shadow-sm"
                    : "text-muted-foreground hover:bg-secondary hover:text-foreground"
                )
              }
            >
              <n.icon className="h-4.5 w-4.5" style={{ width: 18, height: 18 }} />
              {n.label}
            </NavLink>
          ))}
        </nav>
        <div className="p-4 border-t border-border text-[11px] text-muted-foreground">
          <div className="flex items-center gap-1.5"><Warehouse size={13} /> {coops.length} lokasyon</div>
        </div>
      </aside>

      {open && <div className="fixed inset-0 bg-black/40 z-30 lg:hidden" onClick={() => setOpen(false)} />}

      {/* Main */}
      <div className="flex-1 flex flex-col min-w-0">
        <header className="border-b border-border bg-card/80 backdrop-blur sticky top-0 z-20 pt-safe">
          <div className="h-14 sm:h-16 flex items-center justify-between px-3 sm:px-6 gap-2 sm:gap-3">
          <div className="flex items-center gap-3 min-w-0">
            <button className="lg:hidden p-2 -ml-2" onClick={() => setOpen(!open)} data-testid="menu-toggle">
              {open ? <X size={20} /> : <Menu size={20} />}
            </button>
            <div className="hidden sm:block">{coopPicker()}</div>
          </div>
          <div className="flex items-center gap-1 sm:gap-2">
            <SyncManager />
            <div className="relative">
              <button
                onClick={() => setShowAlerts((s) => !s)}
                data-testid="alerts-button"
                className={cn(
                  "relative h-9 w-9 rounded-lg flex items-center justify-center transition-colors",
                  alerts.length ? "bg-destructive/10 text-destructive" : "hover:bg-secondary text-muted-foreground"
                )}
              >
                <AlertTriangle size={18} />
                {alerts.length > 0 && (
                  <span className="absolute -top-1 -right-1 h-4 min-w-4 px-1 rounded-full bg-destructive text-white text-[10px] font-bold flex items-center justify-center">
                    {alerts.length}
                  </span>
                )}
              </button>
              {showAlerts && (
                <div className="absolute right-0 mt-2 w-80 rounded-xl border border-border bg-popover shadow-lg p-2 z-50" data-testid="alerts-panel">
                  <div className="px-2 py-1.5 text-xs font-semibold uppercase tracking-wider text-muted-foreground">Uyarılar</div>
                  {alerts.length === 0 ? (
                    <div className="px-2 py-3 text-sm text-muted-foreground">Aktif uyarı yok.</div>
                  ) : (
                    alerts.map((a, i) => (
                      <div key={i} className={cn(
                        "flex gap-2 px-2 py-2 rounded-lg text-sm",
                        a.severity === "danger" ? "text-destructive" : "text-amber-600 dark:text-amber-400"
                      )}>
                        <AlertTriangle size={16} className="shrink-0 mt-0.5" />
                        <span>{a.message}</span>
                      </div>
                    ))
                  )}
                </div>
              )}
            </div>
            <button
              onClick={toggleTheme}
              data-testid="theme-toggle"
              className="h-9 w-9 rounded-lg flex items-center justify-center hover:bg-secondary text-muted-foreground transition-colors"
            >
              {theme === "dark" ? <Sun size={18} /> : <Moon size={18} />}
            </button>
            <div className="hidden sm:flex flex-col items-end leading-tight pl-1" data-testid="user-info">
              <span className="text-sm font-medium">{user?.name || user?.email}</span>
              <span className="text-[10px] uppercase tracking-wider text-muted-foreground">{user?.role === "admin" ? "Yönetici" : "Kümes Sorumlusu"}</span>
            </div>
            <button
              onClick={() => setShowPw(true)}
              data-testid="change-password-button"
              className="hidden sm:flex h-9 w-9 rounded-lg items-center justify-center hover:bg-secondary text-muted-foreground transition-colors"
              title="Şifre Değiştir"
            >
              <KeyRound size={18} />
            </button>
            <button
              onClick={onBackToModules}
              data-testid="back-to-modules"
              className="h-9 w-9 rounded-lg flex items-center justify-center hover:bg-secondary text-muted-foreground transition-colors"
              title="Modül Seçimi"
            >
              <Warehouse size={18} />
            </button>
            <button
              onClick={logout}
              data-testid="logout-button"
              className="h-9 w-9 rounded-lg flex items-center justify-center hover:bg-destructive/10 hover:text-destructive text-muted-foreground transition-colors"
              title="Çıkış Yap"
            >
              <LogOut size={18} />
            </button>
          </div>
          </div>
          <div className="sm:hidden px-3 pb-2.5" data-testid="mobile-coop-bar">{coopPicker("-mobile")}</div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">{children}</main>
      </div>
      <ChatWidget />
      <InstallPWA />

      {showPw && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4" onClick={() => setShowPw(false)} data-testid="change-password-modal">
          <div className="w-full max-w-sm rounded-xl border border-border bg-card p-6" onClick={(e) => e.stopPropagation()}>
            <div className="flex items-center gap-2 mb-4"><KeyRound size={18} className="text-primary" /><h3 className="font-head font-semibold text-lg">Şifre Değiştir</h3></div>
            <label className="block text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">Mevcut Şifre</label>
            <input type="password" value={pw.current} onChange={(e) => setPw({ ...pw, current: e.target.value })} data-testid="pw-current" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm mb-3 focus:ring-2 focus:ring-primary outline-none" />
            <label className="block text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">Yeni Şifre</label>
            <input type="password" value={pw.next} onChange={(e) => setPw({ ...pw, next: e.target.value })} data-testid="pw-next" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm mb-3 focus:ring-2 focus:ring-primary outline-none" />
            <label className="block text-[11px] uppercase tracking-wide font-semibold text-muted-foreground mb-1.5">Yeni Şifre (Tekrar)</label>
            <input type="password" value={pw.confirm} onChange={(e) => setPw({ ...pw, confirm: e.target.value })} data-testid="pw-confirm" className="h-10 w-full rounded-md border border-input bg-background px-3 text-sm mb-5 focus:ring-2 focus:ring-primary outline-none" />
            <div className="flex justify-end gap-2">
              <button onClick={() => setShowPw(false)} className="h-9 px-4 rounded-full text-sm font-medium hover:bg-secondary transition-colors">İptal</button>
              <button onClick={changePw} data-testid="pw-save" className="h-9 px-5 rounded-full bg-primary text-primary-foreground text-sm font-semibold hover:bg-primary/90 transition-colors">Güncelle</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
